import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * A playback segment on the global report timeline.
 * `startMs` is absolute timeline offset.
 */
export interface AudioTimelineSegment {
  url: string;
  startMs: number;
  endMs?: number;
  durationMs?: number;
  seq?: number;
}

interface AudioPlayerState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  isLoading: boolean;
  error: string | null;
  volume: number;
  isMuted: boolean;
}

export interface AudioPlayerControls {
  play: () => void;
  pause: () => void;
  seek: (timeMs: number) => void;
  subscribeTime: (callback: (timeMs: number) => void) => () => void;
  setAudioTimeline: (segments: AudioTimelineSegment[] | null) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
}

type AudioPlayerContextValue = AudioPlayerState & AudioPlayerControls;

const AudioPlayerStateContext = createContext<AudioPlayerState | null>(null);
const AudioPlayerControlsContext = createContext<AudioPlayerControls | null>(
  null,
);

const DEFAULT_STATE: AudioPlayerState = {
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  isLoading: false,
  error: null,
  volume: 1,
  isMuted: false,
};

const normalizeTimeline = (
  segments: AudioTimelineSegment[] | null,
): AudioTimelineSegment[] => {
  if (!segments || segments.length === 0) return [];

  return segments
    .filter((segment) => segment.url.length > 0)
    .map((segment) => ({
      ...segment,
      startMs: Math.max(0, Math.round(segment.startMs)),
      endMs:
        segment.endMs !== undefined
          ? Math.max(0, Math.round(segment.endMs))
          : undefined,
      durationMs:
        segment.durationMs !== undefined
          ? Math.max(0, Math.round(segment.durationMs))
          : undefined,
    }))
    .sort((a, b) => a.startMs - b.startMs);
};

const getSegmentEndMs = (
  timeline: AudioTimelineSegment[],
  index: number,
  fallbackLastDurationMs?: number,
): number => {
  const segment = timeline[index];
  if (!segment) return 0;
  if (segment.endMs !== undefined) return segment.endMs;
  if (segment.durationMs !== undefined)
    return segment.startMs + segment.durationMs;
  if (index < timeline.length - 1) return timeline[index + 1].startMs;
  if (fallbackLastDurationMs !== undefined) {
    return segment.startMs + fallbackLastDurationMs;
  }
  return segment.startMs;
};

const getTimelineDuration = (
  timeline: AudioTimelineSegment[],
  fallbackLastDurationMs?: number,
): number => {
  if (timeline.length === 0) return 0;
  return Math.max(
    0,
    getSegmentEndMs(timeline, timeline.length - 1, fallbackLastDurationMs),
  );
};

const findSegmentIndexForTime = (
  timeline: AudioTimelineSegment[],
  timeMs: number,
): number => {
  if (timeline.length === 0) return -1;

  for (let i = timeline.length - 1; i >= 0; i -= 1) {
    if (timeMs >= timeline[i].startMs) {
      return i;
    }
  }

  return 0;
};

export const AudioPlayerProvider = ({ children }: { children: ReactNode }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<AudioPlayerState>(DEFAULT_STATE);

  const timeSubscribersRef = useRef<Set<(timeMs: number) => void>>(new Set());
  const rafRef = useRef<number | null>(null);
  const lastNonZeroVolumeRef = useRef(1);

  const timelineRef = useRef<AudioTimelineSegment[]>([]);
  const activeSegmentIndexRef = useRef(0);
  const pendingTimelineRef = useRef<AudioTimelineSegment[] | null | undefined>(
    undefined,
  );
  const pendingSeekGlobalMsRef = useRef<number | null>(null);
  const shouldAutoPlayAfterLoadRef = useRef(false);

  const emitTime = useCallback((timeMs: number) => {
    const subscribers = timeSubscribersRef.current;
    if (subscribers.size === 0) return;

    subscribers.forEach((callback) => {
      try {
        callback(timeMs);
      } catch (error) {
        console.error('Error in time subscriber:', error);
      }
    });
  }, []);

  const computeCurrentGlobalTime = useCallback((audio: HTMLAudioElement) => {
    const localMs = (audio.currentTime || 0) * 1000;
    const timeline = timelineRef.current;
    if (timeline.length === 0) return localMs;

    const index = activeSegmentIndexRef.current;
    const segment = timeline[index];
    if (!segment) return localMs;

    return segment.startMs + localMs;
  }, []);

  const clearLoadedAudio = useCallback(() => {
    const audio = audioRef.current;
    timelineRef.current = [];
    activeSegmentIndexRef.current = 0;
    pendingSeekGlobalMsRef.current = null;
    shouldAutoPlayAfterLoadRef.current = false;

    if (audio) {
      audio.pause();
      audio.src = '';
    }

    setState((prev) => ({
      ...prev,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      isLoading: false,
      error: null,
    }));
  }, []);

  const loadTimelineSegment = useCallback(
    (
      index: number,
      options?: {
        targetGlobalMs?: number;
        autoPlay?: boolean;
      },
    ) => {
      const audio = audioRef.current;
      const timeline = timelineRef.current;
      if (!audio || timeline.length === 0) return;
      if (index < 0 || index >= timeline.length) return;

      const segment = timeline[index];
      const targetGlobalMs = options?.targetGlobalMs ?? segment.startMs;

      activeSegmentIndexRef.current = index;
      pendingSeekGlobalMsRef.current = targetGlobalMs;
      shouldAutoPlayAfterLoadRef.current = options?.autoPlay ?? false;

      setState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
        currentTime: Math.max(0, targetGlobalMs),
        duration: getTimelineDuration(timeline),
      }));

      audio.src = segment.url;
      audio.load();
    },
    [],
  );

  const setAudioTimeline = useCallback(
    (segments: AudioTimelineSegment[] | null) => {
      const normalizedTimeline = normalizeTimeline(segments);
      const audio = audioRef.current;

      if (!audio) {
        pendingTimelineRef.current = normalizedTimeline;
        if (normalizedTimeline.length === 0) {
          setState((prev) => ({
            ...prev,
            isPlaying: false,
            currentTime: 0,
            duration: 0,
            isLoading: false,
            error: null,
          }));
        }
        return;
      }

      if (normalizedTimeline.length === 0) {
        clearLoadedAudio();
        return;
      }

      timelineRef.current = normalizedTimeline;
      activeSegmentIndexRef.current = 0;
      audio.pause();
      loadTimelineSegment(0, {
        targetGlobalMs: normalizedTimeline[0].startMs,
        autoPlay: false,
      });
    },
    [clearLoadedAudio, loadTimelineSegment],
  );

  useEffect(() => {
    const audio = new Audio();
    audio.style.display = 'none';
    audio.volume = 1;
    audio.muted = false;
    document.body.appendChild(audio);
    audioRef.current = audio;

    const reportTime = () => {
      const currentMs = computeCurrentGlobalTime(audio);
      setState((prev) => ({ ...prev, currentTime: currentMs }));
      emitTime(currentMs);
    };

    const handleLoadedMetadata = () => {
      const timeline = timelineRef.current;
      if (timeline.length === 0) {
        setState((prev) => ({
          ...prev,
          duration: (audio.duration || 0) * 1000,
          isLoading: false,
          error: null,
        }));
        return;
      }

      const activeIndex = activeSegmentIndexRef.current;
      const activeSegment = timeline[activeIndex];
      if (!activeSegment) {
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      const requestedGlobalMs =
        pendingSeekGlobalMsRef.current ?? activeSegment.startMs;
      const requestedLocalMs = Math.max(
        0,
        requestedGlobalMs - activeSegment.startMs,
      );
      const localDurationMs = Number.isFinite(audio.duration)
        ? (audio.duration || 0) * 1000
        : 0;
      const clampedLocalMs =
        localDurationMs > 0
          ? Math.min(requestedLocalMs, localDurationMs)
          : requestedLocalMs;

      audio.currentTime = clampedLocalMs / 1000;
      pendingSeekGlobalMsRef.current = null;

      const durationMs = getTimelineDuration(
        timeline,
        activeIndex === timeline.length - 1 ? localDurationMs : undefined,
      );
      const currentGlobalMs = activeSegment.startMs + clampedLocalMs;

      setState((prev) => ({
        ...prev,
        currentTime: currentGlobalMs,
        duration: durationMs,
        isLoading: false,
        error: null,
      }));
      emitTime(currentGlobalMs);

      if (shouldAutoPlayAfterLoadRef.current) {
        shouldAutoPlayAfterLoadRef.current = false;
        audio.play().catch((error) => {
          setState((prev) => ({
            ...prev,
            isPlaying: false,
            error: error?.message || 'Failed to play audio',
          }));
        });
      }
    };

    const handleEnded = () => {
      const timeline = timelineRef.current;
      if (timeline.length === 0) {
        setState((prev) => ({ ...prev, isPlaying: false }));
        return;
      }

      const nextIndex = activeSegmentIndexRef.current + 1;
      if (nextIndex < timeline.length) {
        loadTimelineSegment(nextIndex, {
          targetGlobalMs: timeline[nextIndex].startMs,
          autoPlay: true,
        });
        return;
      }

      const durationMs = getTimelineDuration(
        timeline,
        Number.isFinite(audio.duration)
          ? (audio.duration || 0) * 1000
          : undefined,
      );

      setState((prev) => ({
        ...prev,
        isPlaying: false,
        currentTime: durationMs,
        duration: durationMs,
      }));
      emitTime(durationMs);
    };

    const handleError = () => {
      // Ignore errors from intentional audio clearing (empty src triggers browser error)
      if (timelineRef.current.length === 0) return;

      let errorMsg = 'Failed to load audio';
      if (audio.error?.message) {
        errorMsg = audio.error.message;
      }
      setState((prev) => ({
        ...prev,
        error: errorMsg,
        isLoading: false,
        isPlaying: false,
      }));
    };

    const handlePlay = () => {
      setState((prev) => ({ ...prev, isPlaying: true, error: null }));
      const tick = () => {
        reportTime();
        rafRef.current = requestAnimationFrame(tick);
      };
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(tick);
    };

    const handlePause = () => {
      setState((prev) => ({ ...prev, isPlaying: false }));
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const handleSeeking = () => {
      reportTime();
    };

    const handleVolumeChange = () => {
      if (audio.volume > 0) {
        lastNonZeroVolumeRef.current = audio.volume;
      }

      setState((prev) => ({
        ...prev,
        volume: audio.volume,
        isMuted: audio.muted || audio.volume === 0,
      }));
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('seeking', handleSeeking);
    audio.addEventListener('volumechange', handleVolumeChange);

    if (pendingTimelineRef.current !== undefined) {
      const pendingTimeline = pendingTimelineRef.current;
      pendingTimelineRef.current = undefined;
      setAudioTimeline(pendingTimeline ?? null);
    }

    return () => {
      audio.pause();
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('seeking', handleSeeking);
      audio.removeEventListener('volumechange', handleVolumeChange);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      document.body.removeChild(audio);
    };
  }, [
    computeCurrentGlobalTime,
    emitTime,
    loadTimelineSegment,
    setAudioTimeline,
  ]);

  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (timelineRef.current.length === 0) return;

    audio.play().catch((error) => {
      setState((prev) => ({
        ...prev,
        error: error?.message || 'Failed to play audio',
      }));
    });
  }, []);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
  }, []);

  const seek = useCallback(
    (timeMs: number) => {
      const audio = audioRef.current;
      if (!audio) return;
      if (!Number.isFinite(timeMs)) return;

      const timeline = timelineRef.current;
      if (timeline.length === 0) return;

      const durationMs = getTimelineDuration(timeline);
      const clampedGlobalMs =
        durationMs > 0
          ? Math.max(0, Math.min(timeMs, durationMs))
          : Math.max(0, timeMs);

      const targetIndex = findSegmentIndexForTime(timeline, clampedGlobalMs);
      if (targetIndex < 0) return;

      const targetSegment = timeline[targetIndex];
      const targetLocalMs = Math.max(
        0,
        clampedGlobalMs - targetSegment.startMs,
      );
      const shouldResume = !audio.paused;

      if (targetIndex === activeSegmentIndexRef.current) {
        const localDurationMs = Number.isFinite(audio.duration)
          ? (audio.duration || 0) * 1000
          : 0;
        const clampedLocalMs =
          localDurationMs > 0
            ? Math.min(targetLocalMs, localDurationMs)
            : targetLocalMs;
        audio.currentTime = clampedLocalMs / 1000;

        const currentGlobalMs = targetSegment.startMs + clampedLocalMs;
        setState((prev) => ({
          ...prev,
          currentTime: currentGlobalMs,
          duration: getTimelineDuration(
            timeline,
            targetIndex === timeline.length - 1 ? localDurationMs : undefined,
          ),
        }));
        emitTime(currentGlobalMs);
        return;
      }

      loadTimelineSegment(targetIndex, {
        targetGlobalMs: clampedGlobalMs,
        autoPlay: shouldResume,
      });
    },
    [emitTime, loadTimelineSegment],
  );

  const subscribeTime = useCallback(
    (callback: (timeMs: number) => void): (() => void) => {
      const subscribers = timeSubscribersRef.current;
      subscribers.add(callback);
      return () => {
        subscribers.delete(callback);
      };
    },
    [],
  );

  const setVolume = useCallback((volume: number) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(volume)) return;

    const clampedVolume = Math.max(0, Math.min(volume, 1));
    if (clampedVolume > 0) {
      lastNonZeroVolumeRef.current = clampedVolume;
    }

    audio.muted = clampedVolume === 0;
    audio.volume = clampedVolume;

    setState((prev) => ({
      ...prev,
      volume: clampedVolume,
      isMuted: clampedVolume === 0,
    }));
  }, []);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.muted || audio.volume === 0) {
      const restoreVolume =
        lastNonZeroVolumeRef.current > 0 ? lastNonZeroVolumeRef.current : 0.7;
      audio.muted = false;
      audio.volume = restoreVolume;
      setState((prev) => ({
        ...prev,
        volume: restoreVolume,
        isMuted: false,
      }));
      return;
    }

    if (audio.volume > 0) {
      lastNonZeroVolumeRef.current = audio.volume;
    }
    audio.muted = true;
    setState((prev) => ({ ...prev, isMuted: true }));
  }, []);

  const controls = useMemo<AudioPlayerControls>(
    () => ({
      play,
      pause,
      seek,
      subscribeTime,
      setAudioTimeline,
      setVolume,
      toggleMute,
    }),
    [play, pause, seek, subscribeTime, setAudioTimeline, setVolume, toggleMute],
  );

  return (
    <AudioPlayerControlsContext.Provider value={controls}>
      <AudioPlayerStateContext.Provider value={state}>
        {children}
      </AudioPlayerStateContext.Provider>
    </AudioPlayerControlsContext.Provider>
  );
};

export const useAudioControls = (): AudioPlayerControls => {
  const controls = useContext(AudioPlayerControlsContext);

  if (!controls) {
    throw new Error('useAudioControls must be used within AudioPlayerProvider');
  }

  return controls;
};

export const useAudioPlayer = (): AudioPlayerContextValue => {
  const state = useContext(AudioPlayerStateContext);
  const controls = useAudioControls();

  if (!state) {
    throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  }

  return { ...state, ...controls };
};
